import { useState } from "react";
import { useSelector } from "react-redux";
import { ArrowsLeftRight } from "@phosphor-icons/react";
import LoaderLoop from "../shared/loader/LoaderLoop";
import ModalCloseButton from "../ui/ModalCloseButton";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectAssignableTherapists,
} from "../../store/slices/authSlice";
import { useTransferPatientsMutation } from "../../store/services/therapistApi";
import { useToast } from "../../contexts/ToastContext";
import { memberName } from "./teamUtils";

/**
 * Bulk transfer of (selected) patients from one therapist to another.
 * `patientIds` null ⇒ all patients of `fromTherapist` (legacy behavior).
 * Past/completed appointments never move; future ones move when the
 * checkbox is on (default).
 */
function TransferPatientsModal({ fromTherapist, patientIds = null, onClose, onDone }) {
  const clinicId = useSelector(selectClinicId);
  const actorId = useSelector(selectTherapistId);
  const actorName = useSelector(selectTherapistName);
  const assignable = useSelector(selectAssignableTherapists);
  const { showSuccess, showError } = useToast();
  const [transferPatients, { isLoading }] = useTransferPatientsMutation();

  const fromId = fromTherapist?.uid || fromTherapist?.id;
  const targets = assignable.filter((t) => (t.uid || t.id) !== fromId);
  const [toId, setToId] = useState("");
  const [moveFuture, setMoveFuture] = useState(true);

  const count = Array.isArray(patientIds) ? patientIds.length : null;

  const handleSubmit = async () => {
    if (!toId) return;
    try {
      const res = await transferPatients({
        clinicId,
        fromTherapistId: fromId,
        toTherapistId: toId,
        patientIds,
        transferFutureAppointments: moveFuture,
        actorId,
        actorName,
      }).unwrap();
      showSuccess(
        `${res?.patientsTransferred ?? 0} מטופלים הועברו${
          moveFuture ? ` · ${res?.appointmentsTransferred ?? 0} תורים עתידיים` : ""
        }`
      );
      onDone?.(res);
      onClose();
    } catch (err) {
      console.error("[CK-5541] Error transferring patients:", {
        errorCode: "CK-5541",
        errorMessage: err?.message,
        clinicId,
        fromTherapistId: fromId,
        toTherapistId: toId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהעברת המטופלים. נסו שוב.");
    }
  };

  return (
    <div className="tm-overlay" onClick={onClose}>
      <div className="tm-modal narrow" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="tm-modal-header">
          <div className="tm-modal-title">
            <span className="tm-modal-title-icon">
              <ArrowsLeftRight size={20} weight="fill" />
            </span>
            <div className="tm-modal-title-text">
              <span className="tm-eyebrow">צוות הקליניקה</span>
              <h3>העברה למטפל/ת אחר/ת</h3>
            </div>
          </div>
          <div className="tm-modal-header-actions">
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        <div className="tm-body">
          <p className="tm-caption" style={{ fontSize: "0.86rem", color: "var(--color-text-primary)" }}>
            {count === null
              ? `כל המטופלים של ${memberName(fromTherapist)} יועברו למטפל/ת שתבחר/י.`
              : `${count} מטופלים של ${memberName(fromTherapist)} יועברו למטפל/ת שתבחר/י.`}
          </p>

          <div className="tm-field">
            <label className="tm-label required">אל מטפל/ת</label>
            {targets.length === 0 ? (
              <div className="tm-empty">אין מטפל/ת פעיל/ה נוסף/ת בקליניקה</div>
            ) : (
              <select className="tm-select" value={toId} onChange={(e) => setToId(e.target.value)}>
                <option value="">בחר/י מטפל/ת</option>
                {targets.map((t) => (
                  <option key={t.uid || t.id} value={t.uid || t.id}>
                    {memberName(t)}
                  </option>
                ))}
              </select>
            )}
          </div>

          <label className="tm-check">
            <input type="checkbox" checked={moveFuture} onChange={(e) => setMoveFuture(e.target.checked)} />
            <span>העבר/י גם תורים עתידיים</span>
          </label>
          <span className="tm-caption">תורים שהסתיימו או בוטלו לעולם אינם מועברים.</span>
        </div>

        <div className="tm-footer">
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={isLoading}>
            ביטול
          </button>
          <button type="button" className="tm-btn primary" onClick={handleSubmit} disabled={!toId || isLoading}>
            {isLoading ? <LoaderLoop size="xs" /> : <ArrowsLeftRight size={16} weight="bold" />}
            {isLoading ? "מעביר..." : "העברה"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TransferPatientsModal;
